import { html, nothing } from 'lit';
import { customElement, state } from 'lit/decorators.js';

import '@awesome.me/webawesome/dist/components/button/button.js';
import '@awesome.me/webawesome/dist/components/callout/callout.js';

import { nsLayoutStyles } from '../../../../ns-styles/index.js';

import NSJobComponent from '../../job.js';

import JobSchema from '../../../../models/job-schema.js';

import { callLambda } from '../../../../helpers/lambda.js';
import { claimJob, isUnclaimed } from '../../../../helpers/job-claim.js';
import { lambdaRefusal } from '../../../../helpers/public-ledger-balance-api.js';

import '../../../../ns-elements/ns-job-layout.js';
import '../../../../ns-elements/ns-detail-list.js';
import definition from '@job-schemas/support/companies-house-failing.json';
import { jobSchema } from '../../job-schema.js';
import { failingView } from './behaviour-score-failing-logic.js';
import { recheckNowResult } from './companies-house-failing-logic.js';
import type { LambdaAnswer, RescoreResult } from './trust-score-review-logic.js';

/**
 * The nodeStream support card the trust-score/review-queue cron raises when a company's Companies House check has failed
 * without a break for 12 hours (a failed read of the register or a failed scoring, retried within hours; never a rate limit),
 * so its Companies House score has stopped moving or was never worked out. Support claims it, finds out why (the reason is the
 * last failure's), can re-check Companies House at once (companies-house/trust-score/rescore), and marks it resolved. One card
 * per run of failures: the next score that works ends the run.
 */
@customElement(NSSupportCompaniesHouseFailing.is)
export default class NSSupportCompaniesHouseFailing extends NSJobComponent {
  static get is() {
    return 'ns-support-companies-house-failing';
  }

  static get schema(): JobSchema {
    return jobSchema(definition, { outcomes: { resolved: { type: 'hidden' } } });
  }

  static styles = [nsLayoutStyles, ...super.styles];

  @state()
  private __busy = false;

  @state()
  private __result: RescoreResult | null = null;

  private async __claim() {
    const token = this._auth?.token;
    if (!this._job || !token || this.__busy) return;
    this.__busy = true;
    this.clearError();
    try {
      await claimJob(token, this._job.id);
      this._job = { ...this._job, assignedTo: this._directory?.me?.id };
    } catch (err) {
      this.showError(lambdaRefusal(err, 'Unable to claim this job.'));
    } finally {
      this.__busy = false;
    }
  }

  private async __recheckNow() {
    const companyId = failingView(this._job?.inputValues).companyId;
    if (!companyId || this.__busy) return;
    this.__busy = true;
    this.clearError();
    this.__result = null;
    this.loading('Re-checking Companies House');
    try {
      const response = await callLambda(
        'companies-house/trust-score/rescore',
        'POST',
        this._auth?.token,
        JSON.stringify({ companyId }),
      );
      this.__result = recheckNowResult(response.res as unknown as LambdaAnswer);
    } catch (err) {
      this.__result = {
        ok: false,
        message: lambdaRefusal(err, 'It failed again. Check the lambda logs for the reason, then try again.'),
      };
    } finally {
      this.loaded();
      this.__busy = false;
    }
  }

  private async __resolved() {
    if (this.__busy) return;
    this.__busy = true;
    this.clearError();
    try {
      await this.outcome('resolved');
    } catch (err) {
      this.showError(err, 'Unable to mark this resolved. Please try again.');
    } finally {
      this.__busy = false;
    }
  }

  render() {
    const view = failingView(this._job?.inputValues);
    const result = this.__result;
    return html`
      <ns-job-layout>
        <p slot="intro">${view.companyName}'s Companies House check keeps failing.</p>
        <div class="job-panel">
          ${result
            ? html`
                <wa-callout variant=${result.ok ? 'success' : 'danger'} appearance="filled-outlined">
                  ${result.message}
                </wa-callout>
              `
            : nothing}
          <ns-detail-list
            .rows=${[
              { label: 'Company', value: view.companyName },
              { label: 'Company ID', value: view.companyId },
              { label: 'Failing since', value: view.since ?? 'Unknown' },
              { label: 'Attempts failed in a row', value: view.failures === null ? 'Unknown' : String(view.failures) },
              { label: 'Last failure', value: view.reason ?? 'Unknown' },
            ]}
          ></ns-detail-list>
          <p>
            Its Companies House trust score is worked out from the live register. For this company every attempt has
            failed for at least 12 hours, so its score shows the last one that worked, or none yet. Find out why from
            the last failure, then re-check it. Mark this resolved once it works or you know why it doesn't.
          </p>
        </div>
        <div slot="aside" class="primary-info">
          <span class="info-icon" aria-hidden="true">i</span>
          <h3>What happens next</h3>
          <p>
            The check keeps being retried. A re-check that works sends the new score to the trust-score review queue
            unless it matches the one already reviewed. The next score that works ends this run of failures, and a new
            run of failures raises a new card.
          </p>
        </div>
      </ns-job-layout>
    `;
  }

  renderActions() {
    const job = this._job;
    if (!job || job.status === 'COMPLETE') return nothing;
    if (isUnclaimed(job)) {
      return html`
        <wa-button variant="brand" type="button" ?disabled=${this.__busy} @click=${() => this.__claim()}>
          Claim
        </wa-button>
      `;
    }
    return html`
      <wa-button
        variant="neutral"
        appearance="outlined"
        type="button"
        ?disabled=${this.__busy}
        @click=${() => this.__recheckNow()}
      >
        Re-check now
      </wa-button>
      <wa-button variant="brand" type="button" ?disabled=${this.__busy} @click=${() => this.__resolved()}>
        Resolved
      </wa-button>
    `;
  }
}
